import { computed } from "vue"
import { useTitle } from "@vueuse/core"
import { useRoute } from "vue-router"
import type { Address } from "@types"
import { DEFAULT_SCOPE_NAME } from "@core/constants"
import { formatIdSource } from "@core/helpers"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useConfigStore } from "@core/stores/config.store"
import { useSourceStore } from "@core/stores/source.store"

/**
 * Libellés français pour le titre du document quand aucun contexte bâtiment/source n'est disponible.
 */
const ROUTE_TITLE_BY_NAME: Record<string, string> = {
    Home: "Accueil",
    PageDashboardEM: "Tableau de bord Energy Manager",
    PageDashboardIot: "Tableau de bord IoT",
    PageBuildingConsoChauffage: "Conso. chauffage",
    PageReglageChaufferie: "Réglage de la chaufferie",
    PageBuildingAnalyseTempAmbiance: "Analyse température ambiante",
    PageEnergyManagerEvolutionConso: "Évolution de la conso.",
    PageBuildingAlarm: "Alarmes",
    PageSourceAlarm: "Alarmes",
    PageBuildingOptimisationContractuelle: "Optimisation contractuelle",
    PageDashboardOptimisationContractuelle:
        "Dashboard optimisation contractuelle",
    PageBuildingPVWrapper: "Photovoltaïque",
    PageBuildingPVConsommation: "Conso. et extrapolation PV",
    PageBuildingPVEtudeEconomique: "Étude économique PV",
    PageBuildingPVDimensionnement: "Dimensionnement photovoltaïque",
    PageBuildingPVHistorique: "Historique des études PV",
    PageResidualPower: "Analyse I.R.V.E",
    PageBuildingMeter: "Compteurs",
    PageBuildingPartage: "Partage d'accès",
    PageBuildingSensorList: "Objets connectés",
    PageBuildingWeatherStation: "Station météo",
    PageBuildingInfoGeneral: "Informations générales",
    PageUserAlarm: "Alarmes",
    PageUserSettings: "Paramètres",
    PageSourceList: "Sources",
    PageBuildingAppend: "Ajouter un bâtiment",
    PageBuildingGroup: "Groupe de bâtiments",
    PageBuildingAppendMultiMeters: "Ajout multi-compteurs",
    PageSourceInfoGenerale: "Informations générales",
    PageLogin: "Connexion",
    PageAuthChangePassword: "Changement de mot de passe",
    PageAuthValidationAccount: "Validation du compte",
    PageChangeSpace: "Changer d'espace",
    PageSubscriptionCancelled: "Abonnement résilié",
    PageConsent: "Consentement",
    PageConsentSignInvitation: "Signature du consentement",
    PageConsentShareInvitation: "Partage du consentement",
    PageConsentExtendInvitation: "Extension du consentement",
    PageConsentChangeOwnerInvitation: "Changement de propriétaire",
    PageConsentSignMulti: "Signature multi-consentements",
    PageNotFound: "Page introuvable",
    PageHomeBuildings: "Mes bâtiments",
    PageHomeFolderRoot: "Dossier",
    PageHomeFolder: "Dossier",
    PageCompany: "Entreprise",
    PageInscription: "Inscription",
    PageBuildingProfilUtilisation: "Profil d'utilisation",
    PageBuildingData: "Données",
    PageSourceData: "Données",
    PageComparateurOffres: "Comparateur d'offres",
}

/**
 * Aligné sur la sidebar bâtiment : nom affichable ou adresse.
 */
function formatBuildingAddressLine(address: Address | undefined): string {
    if (!address) return ""
    if (address.inline_name) return address.inline_name
    const parts = [
        address.street,
        [address.zipcode, address.city].filter(Boolean).join(" "),
    ].filter(Boolean)
    return parts.join(", ")
}

/**
 * Libellé français associé au `name` de route (voir {@link ROUTE_TITLE_BY_NAME}).
 * Réutilisable pour l’en-tête bâtiment des sous-pages (ex. module PV).
 */
export function getRoutePageTitle(
    routeName: string | symbol | null | undefined
): string {
    if (routeName == null || typeof routeName === "symbol") {
        return ""
    }
    return ROUTE_TITLE_BY_NAME[routeName] ?? String(routeName)
}

/**
 * Met à jour `document.title` selon la route : contexte bâtiment (nom ou adresse),
 * contexte source (numéro / nom), sinon libellé de page puis suffixe « | … ».
 * Le suffixe est « Homeys » pour le scope par défaut ({@link DEFAULT_SCOPE_NAME}), sinon marque ou nom du scope.
 */
export function useDocumentTitle() {
    const route = useRoute()
    const buildingStore = useBuildingStore()
    const sourceStore = useSourceStore()
    const configStore = useConfigStore()

    /**
     * Nom d’app affiché dans l’onglet (aligné sur PageSubscriptionCancelled / header).
     */
    const appTitleSuffix = computed(() => {
        const scopeName = configStore.getScopeName
        if (scopeName == null || scopeName === DEFAULT_SCOPE_NAME) {
            return "Homeys"
        }
        return configStore.getScopeBrand?.trim() || scopeName.trim() || "Homeys"
    })

    const pageTitle = computed(() => {
        if (route.meta.documentTitle?.trim()) {
            return route.meta.documentTitle.trim()
        }

        const idBuilding = route.params._id_building as string | undefined
        const idSource = route.params._id_source as string | undefined

        if (idBuilding) {
            const b = buildingStore.building
            const matches =
                b && (b._id_building === idBuilding || b._id === idBuilding)
            if (matches) {
                const name = b.name?.trim()
                if (name) return name
                const line = formatBuildingAddressLine(b.address)
                if (line) return line
            }
            return getRoutePageTitle(route.name)
        }

        if (idSource) {
            const s = sourceStore.getSourceById(idSource)
            if (s) {
                const typeStr = (s.type ?? s.idtype ?? "") as string
                const num = formatIdSource(s.idsource ?? "", typeStr)
                const name = s.name?.trim()
                if (num && name) return `${num} / ${name}`
                if (name) return name
                if (num) return num
            }
            return getRoutePageTitle(route.name)
        }

        return getRoutePageTitle(route.name)
    })

    const fullDocumentTitle = computed(() => {
        const ctx = pageTitle.value.trim()
        const suffix = appTitleSuffix.value
        if (!ctx) return suffix
        return `${ctx} | ${suffix}`
    })

    useTitle(fullDocumentTitle)
}
